<template>
  <v-dialog v-model="show" persistent max-width="675px">
    <v-card>
      <v-card-title>
        <v-layout>
          <v-flex>
            <span class="screen-title component">{{ workflowLabel(title) }}</span>
            <div class="mt-8 title-description">
              {{ adminText('workflowsAll.showcase_sponsors_with_their_logo_website_and_9923181', 'Showcase sponsors with their logo, website, and a short description in one consistent, easy-to-manage format.') }}
            </div>
          </v-flex>
          <v-flex shrink>
            <v-btn icon @click="closeDialog">
              <v-icon color="#979797">clear</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-title>

      <v-card-text>
        <sponsor-form
          v-if="show"
          :initial-values="form"
          :event-id="eventId"
          :vs="$v"
          @values="form = $event"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <main-button
          v-if="action === 'update'"
          class="mr-16"
          flat
          color="error"
          :text="adminText('workflowsAll.delete_f6fdbe4', 'Delete')"
          @click="deleteDialog = true"
        />
        <main-button class="mr-16" flat :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" @click.stop="closeDialog" />
        <main-button depressed color="primary" :text="workflowLabel(okText)" @click="submitForm" />
      </v-card-actions>
    </v-card>

    <delete-dialog
      :show.sync="deleteDialog"
      :title="adminText('workflowsAll.delete_sponsor_0effb9e', 'Delete sponsor')"
      :content="workflowLabel(('' + (form.name || adminText('workflowsAll.this_sponsor_da19fd2', 'This sponsor')) + adminText('workflowsAll.will_be_removed_from_the_event_and_from_the_m_0f9160d', ' will be removed from the event and from the mobile app.')))"
      :on-delete="deleteSponsor"
    />
  </v-dialog>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { required, url } from 'vuelidate/lib/validators'

import DeleteDialog from '../shared/dialogs/delete-dialog.vue'
import SponsorForm from './sponsor-form.vue'
import {
  CREATE_EVENT_SPONSOR,
  DELETE_EVENT_SPONSOR,
  UPDATE_EVENT_SPONSOR,
} from '../../graphql/sponsors.graphql'
import { submitGuard } from '../../utils'

const emptySponsor = () => ({ name: '', logo: null, sponsorLink: '' })

/**
 * COR-1684: add or edit one sponsor. Each save commits on its own - there is no page-level
 * save on the sponsors screen - and the list refetches on `saved` / `deleted`.
 *
 * `rank`, `description` and `logoBackground` are columns the organizer does not set: ranks
 * are off unless an event asks for them, and the logo plate is LIGHT for every logo but a
 * white-ink one. Leaving them out of the input keeps a hand-set value intact, because the
 * backend merges the input into the row.
 */
export default {
  mixins: [WorkflowDisplay],
  name: 'sponsor-dialog',
  components: {
    DeleteDialog,
    SponsorForm,
  },
  props: {
    show: Boolean,
    action: {
      type: String,
      default: 'add',
      validator: value => ['add', 'update'].indexOf(value) !== -1,
    },
    sponsor: {
      type: Object,
      default: null,
    },
    eventId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      form: this.sponsor ? { ...this.sponsor } : emptySponsor(),
      deleteDialog: false,
      submitInProgress: false,
    }
  },
  computed: {
    title() {
      return this.action === 'add' ? this.adminText('workflowsAll.add_sponsors_aa09d70', 'Add sponsors') : this.adminText('workflowsAll.update_sponsor_50b5dce', 'Update sponsor')
    },
    okText() {
      return this.action === 'add' ? this.adminText('workflowsAll.add_sponsor_581e2ac', 'Add sponsor') : this.adminText('workflowsAll.update_fb91e24', 'Update')
    },
  },
  watch: {
    show(visible) {
      if (visible) {
        this.form = this.sponsor ? { ...this.sponsor } : emptySponsor()
        this.$v.$reset()
      }
    },
  },
  validations() {
    return {
      form: {
        name: { required },
        sponsorLink: { url },
      },
    }
  },
  methods: {
    async submitForm() {
      await submitGuard(this, async () => {
        const input = {
          name: this.form.name,
          logo: this.form.logo || null,
          sponsorLink: this.form.sponsorLink || null,
        }

        if (this.action === 'add') {
          await this.$apollo.mutate({
            mutation: CREATE_EVENT_SPONSOR,
            variables: { eventId: this.eventId, input },
          })
        } else {
          await this.$apollo.mutate({
            mutation: UPDATE_EVENT_SPONSOR,
            variables: { eventId: this.eventId, id: this.form.id, input },
          })
        }

        this.$emit('saved')
        this.closeDialog()
      })
    },
    async deleteSponsor() {
      await this.$apollo.mutate({
        mutation: DELETE_EVENT_SPONSOR,
        variables: { eventId: this.eventId, id: this.form.id },
      })
      this.$emit('deleted')
      this.closeDialog()
    },
    closeDialog() {
      this.$v.$reset()
      this.$emit('update:show', false)
    },
  },
}
</script>

<style scoped>
.mt-8 {
  margin-top: 8px;
}
</style>
